import * as pdfMake from "pdfmake/build/pdfmake";
import * as pdfFonts from "pdfmake/build/vfs_fonts";
import dayjs from "dayjs";

//APP
import { imgLogo } from "../../../../../../../../utils/base64-Imgs";
import { fetchImageAsBase64 } from "../../../../../../../../utils/imageUtils";
import { Abastecimento } from "../../../../../../../../services/abastecimento/GET";
import { dinheiro } from "../../../../../../../../utils";
import { valorDoAbastecimento } from "../../../../../meta";

// `pdfmake` não tem tipagem instalada no projeto (`@types/pdfmake` ausente), então os nós
// do documento são `any` neste arquivo
(pdfMake as any).vfs = pdfFonts.pdfMake.vfs;

const COR_CABECALHO = '#0e743f';
const COR_ZEBRA = '#e5f1e1';
/**Quantos caracteres cabem em uma linha das colunas `Veículo` (18%) e `Responsável` (14%), fonte 9, no
 * pior caso, nome em maiúsculas. Em minúsculas caberia mais, mas o pdfmake não mede texto nem tem reticências
*/
const LIMITE_NOME_VEICULO = 20;
const LIMITE_NOME_RESPONSAVEL = 15;

export interface HeaderDef {
    logo: string;
    titulo: string;
    nome_arquivo?: string;
    razaoSocial: string;
    cliente: string;
    /**Intervalo do filtro avançado já formatado, ou `Todos` sem intervalo aplicado */
    periodo: string;
    /**Local do filtro avançado (`Posto`/`Propriedade`), ou `Todos` sem filtro aplicado */
    local: string;
    autor: string;
}

/**Campo de texto ausente fica visível no relatório como `??` */
function texto(valor?: string): string {
    return String(valor ?? '').trim() === '' ? '??' : String(valor)
}

/**Corta com `...` o texto que passaria do limite, para a célula ficar em uma linha só */
function umaLinha(valor: string, limite: number): string {
    return valor.length > limite ? `${valor.slice(0, limite - 3).trimEnd()}...` : valor
}

/**Mesmo critério de local usado no filtro avançado (`mesmoLocal`): `tipo_local` não vem
 * nos abastecimentos antigos, então `cod_propriedade` também indica propriedade
*/
function emPropriedade(item: Abastecimento): boolean {
    return item?.tipo_local === 'propriedade' || Boolean(item?.cod_propriedade)
}

/**Mesma regra da tag `Acerto` da tabela (`tipoAcerto`) */
function acerto(item: Abastecimento): string {
    if (item?.necessario_reembolso) return 'Reembolso'
    if (item?.desconto_folha) return 'Desc. na folha'
    return 'Empresa'
}

function numeroLitros(qntd_litros: string): number {
    const litros = Number(qntd_litros)
    return String(qntd_litros ?? '').trim() !== '' && isFinite(litros) ? litros : NaN
}

function formatarLitros(litros: number): string {
    if (!isFinite(litros)) return '??'
    return `${litros.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} L`
}

/**Total de valores com a mesma regra do rodapé da tabela (`calcularTotalValores`):
 * abastecimento na propriedade não tem valor total, e valor inválido não entra na soma
*/
function somarValores(abastecimentos: Abastecimento[]): number {
    return abastecimentos.reduce((soma, item) => {
        const valor = Number(item?.valor_total)
        return isFinite(valor) ? soma + valor : soma
    }, 0)
}

function somarLitros(abastecimentos: Abastecimento[]): number {
    return abastecimentos.reduce((soma, item) => {
        const litros = numeroLitros(item?.qntd_litros)
        return isFinite(litros) ? soma + litros : soma
    }, 0)
}

function celulaResumo(rotulo: string, valor: string): any {
    return {
        text: [
            { text: `${rotulo}\n`, fontSize: 8, color: '#7b7b7b', bold: true },
            { text: valor.toUpperCase(), fontSize: 11, bold: true, color: '#000000' }
        ],
        alignment: 'center',
        margin: [5, 5, 5, 5]
    }
}

export const gerarDocDefinition = (abastecimentos: Abastecimento[], headerInfo: HeaderDef) => {
    const content: any[] = [];

    // Header Principal (Somente Primeira Página)
    content.push({
        stack: [
            {
                image: headerInfo?.logo || 'imgLogoAlias',
                width: 80,
                alignment: 'center',
                margin: [0, 0, 0, 10]
            },
            {
                text: headerInfo?.titulo,
                style: 'headerMain',
                alignment: 'center',
                margin: [0, 0, 0, 10]
            },
            {
                canvas: [
                    {
                        type: 'rect',
                        x: 0,
                        y: 0,
                        w: 760,
                        h: 4,
                        linearGradient: ['#e5f1e1', '#5da57d', '#0e743f']
                    }
                ],
            },
            {
                stack: [
                    {
                        text: headerInfo?.razaoSocial,
                        fontSize: 8,
                        bold: true,
                        color: '#4D4D4D',
                        alignment: 'center'
                    },
                    {
                        margin: [0, 10, 0, 0],
                        table: {
                            widths: ['*', '30%', '20%', '15%'],
                            body: [[
                                celulaResumo('CLIENTE', headerInfo?.cliente),
                                celulaResumo('PERÍODO', headerInfo?.periodo),
                                celulaResumo('LOCAL', headerInfo?.local),
                                celulaResumo('REGISTROS', String(abastecimentos.length)),
                            ]]
                        },
                        layout: {
                            hLineWidth: () => 1,
                            vLineWidth: () => 1,
                            hLineColor: () => '#7b7b7b',
                            vLineColor: () => '#7b7b7b'
                        }
                    }
                ],
                margin: [0, 5, 0, 20]
            }
        ],
        margin: [0, 0, 0, 10]
    });

    const colunas = ['Data', 'Frota', 'Veículo', 'Responsável', 'Local', 'Combustível', 'Litros', 'Valor', 'Acerto'];
    const body: any[] = [
        colunas.map((coluna) => ({ text: coluna, style: 'tableHeader', fillColor: COR_CABECALHO, color: 'white' }))
    ];

    abastecimentos.forEach((item, index) => {
        const fillColor = index % 2 === 0 ? null : COR_ZEBRA;
        const celula = (valor: string, alignment = 'left') => ({ text: valor, margin: [2, 4, 2, 4], alignment, fillColor });

        body.push([
            { ...celula(item?.data ? dayjs(item.data).format('DD/MM/YYYY') : '??'), noWrap: true },
            celula(texto(item?.frota)),
            celula(umaLinha(texto(item?.veiculo_nome), LIMITE_NOME_VEICULO)),
            celula(umaLinha(texto(item?.usuario_nome), LIMITE_NOME_RESPONSAVEL)),
            celula(texto(emPropriedade(item) ? item?.propriedade_nome : item?.nome_posto)),
            celula(texto(item?.tipo_combustivel_nome)),
            celula(formatarLitros(numeroLitros(item?.qntd_litros)), 'right'),
            celula(valorDoAbastecimento(item?.valor_total, item?.tipo_local), 'right'),
            celula(acerto(item)),
        ]);
    });

    if (abastecimentos.length === 0) {
        body.push([
            { text: 'Nenhum abastecimento encontrado.', colSpan: colunas.length, margin: [2, 4, 2, 4], alignment: 'center', italics: true },
            ...Array(colunas.length - 1).fill({}),
        ]);
    } else {
        const total = (valor: string, alignment = 'right') => ({ text: valor, bold: true, margin: [2, 4, 2, 4], alignment, fillColor: '#d9d9d9' });

        body.push([
            { ...total('TOTAL', 'left'), colSpan: 6 },
            {}, {}, {}, {}, {},
            total(formatarLitros(somarLitros(abastecimentos))),
            total(dinheiro(somarValores(abastecimentos))),
            total(''),
        ]);
    }

    content.push({
        table: {
            headerRows: 1,
            widths: ['9%', '6%', '18%', '14%', '17%', '10%', '9%', '9%', '8%'],
            body
        },
        margin: [0, 0, 0, 15]
    });

    return {
        info: {
            title: headerInfo?.nome_arquivo || headerInfo?.titulo,
            author: headerInfo?.autor,
        },
        content: content,
        pageOrientation: 'landscape',
        footer: (currentPage: number, pageCount: number) => {
            const dataGeracao = dayjs().format("DD/MM/YYYY, HH:mm:ss");
            return {
                stack: [
                    { text: '', margin: [0, 5, 0, 0] },
                    {
                        canvas: [
                            {
                                type: 'rect',
                                x: 0,
                                y: 0,
                                w: 760,
                                h: 4,
                                linearGradient: ['#e5f1e1', '#5da57d', '#0e743f']
                            }
                        ],
                        margin: [0, 0, 0, 5]
                    },
                    { text: '', margin: [0, 5, 0, 0] },
                    {
                        columns: [
                            {
                                width: '*',
                                stack: [
                                    {
                                        image: 'imgLogoAlias',
                                        width: 50,
                                        margin: [0, 3, 0, 0],
                                    },
                                ],
                                alignment: 'left',
                            },
                            {
                                width: '*',
                                stack: [
                                    {
                                        text: `Gerado por: ${headerInfo?.autor}`,
                                        bold: true,
                                        alignment: 'center',
                                        fontSize: 7,
                                        color: '#4D4D4D'
                                    },
                                    {
                                        text: dataGeracao,
                                        alignment: 'center',
                                        fontSize: 7,
                                        color: '#4D4D4D'
                                    },
                                ],
                                margin: [0, 3, 0, 0],
                                alignment: 'center',
                            },
                            {
                                width: '*',
                                text: `${currentPage} de ${pageCount}`,
                                alignment: 'right',
                                fontSize: 8,
                                margin: [0, 3, 0, 0],
                                color: '#4D4D4D'
                            },
                        ],
                    },
                    {
                        width: '*',
                        text: headerInfo?.razaoSocial,
                        alignment: 'left',
                        fontSize: 6,
                        margin: [0, 3, 0, 10],
                        color: '#4D4D4D'
                    },
                ],
                margin: [40, 0, 40, 0],
            };
        },
        images: {
            imgLogoAlias: imgLogo
        },
        styles: {
            headerMain: {
                fontSize: 20,
                bold: true,
                color: 'black'
            },
            tableHeader: {
                bold: true,
                fontSize: 10,
                color: 'white',
                alignment: 'left'
            }
        },
        defaultStyle: {
            fontSize: 9,
            alignment: 'left'
        },
        pageMargins: [40, 40, 40, 60]
    };
};

/**Gera o PDF e abre a janela de impressão do navegador por um iframe oculto */
export const imprimirPdfAbastecimento = async (abastecimentos: Abastecimento[], headerInfo: HeaderDef): Promise<void> => {
    if (headerInfo?.logo && headerInfo.logo.startsWith("http")) {
        headerInfo.logo = await fetchImageAsBase64(headerInfo.logo);
    }

    const docDefinition = gerarDocDefinition(abastecimentos, headerInfo);

    return new Promise((resolve, reject) => {
        try {
            pdfMake.createPdf(docDefinition).getBlob((blob) => {
                const blobUrl = URL.createObjectURL(blob);

                // o título da página vira o nome do arquivo na janela de impressão/salvamento
                const originalTitle = document.title;
                const fileTitle = headerInfo?.nome_arquivo || headerInfo?.titulo;
                document.title = fileTitle.replace(/[/\\?%*:|"<>]/g, '-');

                const iframe = document.createElement('iframe');
                iframe.style.position = 'fixed';
                iframe.style.top = '0';
                iframe.style.left = '0';
                iframe.style.width = '1px';
                iframe.style.height = '1px';
                iframe.style.opacity = '0.01';
                iframe.style.pointerEvents = 'none';
                iframe.style.border = 'none';
                iframe.src = blobUrl;

                document.body.appendChild(iframe);

                let cleanedUp = false;
                const cleanUp = () => {
                    if (cleanedUp) return;
                    cleanedUp = true;

                    document.title = originalTitle;
                    URL.revokeObjectURL(blobUrl);
                    if (iframe.parentNode) {
                        document.body.removeChild(iframe);
                    }

                    window.removeEventListener('afterprint', cleanUp);
                    window.removeEventListener('focus', cleanUp);
                };

                // restaura o título só quando o diálogo de impressão fecha
                window.addEventListener('afterprint', cleanUp);
                window.addEventListener('focus', cleanUp);

                iframe.onload = () => {
                    // pequeno atraso para o PDF terminar de carregar no iframe
                    setTimeout(() => {
                        try {
                            iframe.contentWindow?.focus();
                            iframe.contentWindow?.print();
                            resolve();
                        } catch (printErr) {
                            cleanUp();
                            reject(printErr);
                        }
                    }, 500);
                };

                iframe.onerror = (err) => {
                    cleanUp();
                    reject(err);
                };
            });
        } catch (err) {
            reject(err);
        }
    });
};
